import { renderRowsWithSpacing } from "@/lib/accordion-content";
import type { ArtworkHistory } from "@/api/artwork/artwork-history";

export type ArtworkHistoryMergedAuction = {
  kind: "auction";
  id: string;
  artworkId: string | null;
  provenanceRank?: number;
  oeuvresDansVentesDescriptionNumeroLot?: string;
  auction: {
    ventesDescriptionCommissaires?: string;
    ventesDescriptionLieu?: string;
    ventesDescriptionVille?: string;
    ventesDescriptionPays?: string;
    ventesDescriptionDate?: string;
    ventesDescriptionTitre?: string;
    ventesDescriptionInclureTitreDansResume?: boolean;
  } | null;
};

type ProvenanceRow =
  | { kind: "history"; index: number; item: ArtworkHistory }
  | { kind: "auction"; index: number; item: ArtworkHistoryMergedAuction };

const cleanParts = (parts: Array<string | null | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean) as string[];

const hasPersistedOrder = (rows: ProvenanceRow[]) =>
  rows.some((row) => (row.item.provenanceRank ?? 0) > 0);

const getFallbackOrder = (row: ProvenanceRow, historyCount: number) => {
  if (row.kind === "history") return row.item.id_imported ?? row.index;
  return historyCount + row.index;
};

export function buildArtworkProvenance({
  history,
  auctions,
  t,
}: {
  history?: ArtworkHistory[];
  auctions?: ArtworkHistoryMergedAuction[];
  t: (key: string) => string;
}) {
  const historyRows: ProvenanceRow[] = (history ?? []).map((item, index) => ({
    kind: "history",
    item,
    index,
  }));
  const auctionRows: ProvenanceRow[] = (auctions ?? []).map((item, index) => ({
    kind: "auction",
    item,
    index,
  }));
  const rows = [...historyRows, ...auctionRows];
  if (rows.length === 0) return null;

  const shouldUsePersistedOrder = hasPersistedOrder(rows);
  rows.sort((a, b) => {
    const aRank = shouldUsePersistedOrder
      ? (a.item.provenanceRank ?? 0)
      : getFallbackOrder(a, historyRows.length);
    const bRank = shouldUsePersistedOrder
      ? (b.item.provenanceRank ?? 0)
      : getFallbackOrder(b, historyRows.length);
    if (aRank !== bRank) return aRank - bRank;
    if (a.kind !== b.kind) return a.kind === "history" ? -1 : 1;
    return (a.item.id ?? "").localeCompare(b.item.id ?? "");
  });

  const renderedRows = rows
    .map((row) => {
      if (row.kind === "history") {
        const item = row.item;
        const location = item.oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere
          ? t("computeField.collectionParticuliere")
          : item.oeuvresLocalisationHistoriqueHistoriqueNom;
        return cleanParts([
          location,
          item.oeuvresLocalisationHistoriqueHistoriqueVille,
          item.oeuvresLocalisationHistoriqueHistoriqueDates,
        ]).join(", ");
      }

      const item = row.item;
      const auction = item.auction;
      const location = cleanParts([
        auction?.ventesDescriptionCommissaires,
        auction?.ventesDescriptionLieu,
        auction?.ventesDescriptionVille,
        auction?.ventesDescriptionPays,
        item.oeuvresDansVentesDescriptionNumeroLot
          ? `lot n°${item.oeuvresDansVentesDescriptionNumeroLot}`
          : null,
      ]).join(", ");
      // Titre de la vente en tête quand la case « Inclure le titre dans le résumé » est cochée.
      return cleanParts([
        auction?.ventesDescriptionInclureTitreDansResume ? auction.ventesDescriptionTitre : null,
        cleanParts([auction?.ventesDescriptionDate, location]).join(" - "),
      ]).join(", ");
    })
    .filter(Boolean);

  return renderedRows.length > 0 ? renderRowsWithSpacing(renderedRows) : null;
}
